/**
 * Google Integration React Query Hooks
 *
 * Provides hooks for managing Google Workspace connection state.
 * OAuth connection/disconnection logic remains in components.
 * This hook only manages server-persisted connection data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';
import { queryClient } from './queryClient';

// =============================================================================
// TYPES (re-exported, now owned here)
// =============================================================================

export type GoogleServiceId = 'gmail' | 'calendar' | 'drive' | 'docs' | 'sheets' | 'meet' | 'contacts';

export interface GoogleServiceState {
  serviceId: GoogleServiceId;
  status: 'active' | 'inactive' | 'error' | 'syncing';
  lastSyncAt?: string;
  syncedCount?: number;
  errorMessage?: string;
}

export interface GoogleConnectionData {
  isConnected: boolean;
  googleEmail: string | null;
  sessionId: string | null;
  authorizedScopes: string[];
  services: Record<GoogleServiceId, GoogleServiceState>;
  connectedAt?: string;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_SERVICE_STATES: Record<GoogleServiceId, GoogleServiceState> = {
  gmail: { serviceId: 'gmail', status: 'inactive' },
  calendar: { serviceId: 'calendar', status: 'inactive' },
  drive: { serviceId: 'drive', status: 'inactive' },
  docs: { serviceId: 'docs', status: 'inactive' },
  sheets: { serviceId: 'sheets', status: 'inactive' },
  meet: { serviceId: 'meet', status: 'inactive' },
  contacts: { serviceId: 'contacts', status: 'inactive' },
};

export const DEFAULT_GOOGLE_CONNECTION: GoogleConnectionData = {
  isConnected: false,
  googleEmail: null,
  sessionId: null,
  authorizedScopes: [],
  services: { ...DEFAULT_SERVICE_STATES },
};

// =============================================================================
// GRAPHQL
// =============================================================================

const GOOGLE_CONNECTION_QUERY = `
  query GetGoogleConnection($userId: String) {
    googleConnection(userId: $userId) {
      id
      userId
      isConnected
      googleEmail
      sessionId
      authorizedScopes
      services {
        serviceId
        status
        lastSyncAt
        syncedCount
        errorMessage
      }
      connectedAt
      updatedAt
    }
  }
`;

const SAVE_GOOGLE_CONNECTION_MUTATION = `
  mutation SaveGoogleConnection($input: GoogleConnectionInput!, $userId: String) {
    saveGoogleConnection(input: $input, userId: $userId) {
      id
      userId
      isConnected
      googleEmail
      sessionId
      authorizedScopes
      services {
        serviceId
        status
        lastSyncAt
        syncedCount
        errorMessage
      }
      connectedAt
      updatedAt
    }
  }
`;

const DELETE_GOOGLE_CONNECTION_MUTATION = `
  mutation DeleteGoogleConnection($userId: String) {
    deleteGoogleConnection(userId: $userId)
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

/**
 * Map GraphQL response to local types
 */
function mapGraphQLConnection(data: any): GoogleConnectionData {
  const services = { ...DEFAULT_SERVICE_STATES };

  if (data.services && Array.isArray(data.services)) {
    for (const s of data.services) {
      const serviceId = s.serviceId as GoogleServiceId;
      services[serviceId] = {
        serviceId,
        status: s.status,
        lastSyncAt: s.lastSyncAt || undefined,
        syncedCount: s.syncedCount ?? undefined,
        errorMessage: s.errorMessage || undefined,
      };
    }
  }

  return {
    isConnected: data.isConnected,
    googleEmail: data.googleEmail || null,
    sessionId: data.sessionId || null,
    authorizedScopes: data.authorizedScopes || [],
    services,
    connectedAt: data.connectedAt || undefined,
  };
}

/**
 * Map local connection data to GraphQL input
 * Note: async-graphql 7.0 uses camelCase for field names by default
 */
function connectionToInput(connection: GoogleConnectionData) {
  return {
    isConnected: connection.isConnected,
    googleEmail: connection.googleEmail,
    sessionId: connection.sessionId,
    authorizedScopes: connection.authorizedScopes,
    services: Object.values(connection.services).map((s) => ({
      serviceId: s.serviceId,
      status: s.status,
      lastSyncAt: s.lastSyncAt || null,
      syncedCount: s.syncedCount ?? null,
      errorMessage: s.errorMessage || null,
    })),
    connectedAt: connection.connectedAt || null,
  };
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch Google connection data from the server.
 * Returns DEFAULT_GOOGLE_CONNECTION if not connected.
 */
export function useGoogleConnection() {
  return useQuery({
    queryKey: queryKeys.google.connection(),
    queryFn: async () => {
      const response = await graphqlFetch<{ googleConnection: any }>(
        GOOGLE_CONNECTION_QUERY,
        { userId: null }
      );
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
      }
      if (response.data?.googleConnection) {
        return mapGraphQLConnection(response.data.googleConnection);
      }
      return DEFAULT_GOOGLE_CONNECTION;
    },
    staleTime: 30 * 60_000, // 30 minutes
  });
}

/**
 * Save Google connection state to the server.
 */
export function useSaveGoogleConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connection: GoogleConnectionData) => {
      const input = connectionToInput(connection);
      const response = await graphqlFetch<{ saveGoogleConnection: any }>(
        SAVE_GOOGLE_CONNECTION_MUTATION,
        { input, userId: null }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.saveGoogleConnection) {
        return mapGraphQLConnection(response.data.saveGoogleConnection);
      }
      throw new Error('Failed to save Google connection');
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.google.connection(), data);
    },
  });
}

/**
 * Delete Google connection (disconnect).
 */
export function useDeleteGoogleConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await graphqlFetch<{ deleteGoogleConnection: boolean }>(
        DELETE_GOOGLE_CONNECTION_MUTATION,
        { userId: null }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deleteGoogleConnection) {
        throw new Error('Failed to delete Google connection');
      }
      return true;
    },
    onSuccess: () => {
      qc.setQueryData(queryKeys.google.connection(), DEFAULT_GOOGLE_CONNECTION);
    },
  });
}

// =============================================================================
// NON-REACT ACCESS (for services)
// =============================================================================

/**
 * Read Google connection from the react-query cache.
 * For use in non-React code (services, utilities).
 * Returns DEFAULT_GOOGLE_CONNECTION if cache is empty.
 */
export function getGoogleConnectionFromCache(): GoogleConnectionData {
  return (
    queryClient.getQueryData<GoogleConnectionData>(queryKeys.google.connection()) ??
    DEFAULT_GOOGLE_CONNECTION
  );
}
